/**
 * curvatureFlowPipeline — Mean curvature flow.
 *
 * A 2D curve evolves under its mean curvature: ∂u/∂t = -|∇u| div(∇u/|∇u|).
 * The curve smooths out over time, eventually becoming a circle.
 * We visualize the level set of a height field as it evolves.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CurvatureFlowPipeline {
  updateProgram: WebGLProgram | null;
  renderProgram: WebGLProgram | null;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  stateTextures: [WebGLTexture | null, WebGLTexture | null];
  stateBuffers: [WebGLFramebuffer | null, WebGLFramebuffer | null];
  pingIdx: number;
  bands: Float32Array;
  gridSize: number;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

// State: R = SDF value (positive outside, negative inside).
const UPDATE_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outState;

uniform sampler2D uPrevState;
uniform float uTime;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uPrevState, 0));
  float u = texture(uPrevState, vUV).r;
  // 4 neighbors.
  float uL = texture(uPrevState, vUV - vec2(texel.x, 0.0)).r;
  float uR = texture(uPrevState, vUV + vec2(texel.x, 0.0)).r;
  float uB = texture(uPrevState, vUV - vec2(0.0, texel.y)).r;
  float uT = texture(uPrevState, vUV + vec2(0.0, texel.y)).r;
  // Gradient magnitude.
  vec2 grad = vec2(uR - uL, uT - uB) * 0.5;
  float gradMag = length(grad) + 0.001;
  // Divergence of (grad / |grad|).
  vec2 normGrad = grad / gradMag;
  // Approximate divergence via the divergence theorem:
  // div(F) ≈ (F_right - F_left + F_top - F_bottom) / (2 * dx)
  // where F = grad / |grad|.
  float nLx = texture(uPrevState, vUV - vec2(2.0 * texel.x, 0.0)).r;
  float nRx = texture(uPrevState, vUV + vec2(2.0 * texel.x, 0.0)).r;
  float nBy = texture(uPrevState, vUV - vec2(0.0, 2.0 * texel.y)).r;
  float nTy = texture(uPrevState, vUV + vec2(0.0, 2.0 * texel.y)).r;
  // Gradient at +2dx.
  vec2 gradL = vec2(u - nLx, (uT - uB) * 0.5) * 0.5;
  vec2 gradR = vec2(nRx - u, (uT - uB) * 0.5) * 0.5;
  vec2 gradB = vec2((uR - uL) * 0.5, u - nBy) * 0.5;
  vec2 gradT = vec2((uR - uL) * 0.5, nTy - u) * 0.5;
  float nrmL = length(gradL) + 0.001;
  float nrmR = length(gradR) + 0.001;
  float nrmB = length(gradB) + 0.001;
  float nrmT = length(gradT) + 0.001;
  vec2 nL = gradL / nrmL;
  vec2 nR = gradR / nrmR;
  vec2 nB = gradB / nrmB;
  vec2 nT = gradT / nrmT;
  float divX = (nR.x - nL.x) / (2.0 * texel.x);
  float divY = (nT.y - nB.y) / (2.0 * texel.y);
  float curv = divX + divY;
  // Mean curvature flow: u_t = -|grad u| * H = -|grad u| * curvature.
  float uNew = u - gradMag * curv * 0.5;
  outState = vec4(uNew, 0.0, 0.0, 1.0);
}
`;

const RENDER_FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform sampler2D uState;
uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  vec2 texel = 1.0 / vec2(textureSize(uState, 0));
  float u = texture(uState, vUV).r;
  vec2 p = vUV * 2.0 - 1.0;
  p.x *= uResolution.x / uResolution.y;
  // Curvature flow: a height field that smooths over time.
  // Compute gradient and curvature for shading.
  float dx = texture(uState, vUV + vec2(texel.x, 0.0)).r - texture(uState, vUV - vec2(texel.x, 0.0)).r;
  float dy = texture(uState, vUV + vec2(0.0, texel.y)).r - texture(uState, vUV - vec2(0.0, texel.y)).r;
  // Background: dark gradient with movement.
  vec3 bg = vec3(0.02, 0.01, 0.05);
  vec3 col = bg + vec3(0.04, 0.02, 0.10) * (1.0 - length(p) * 0.5);
  // Color by height.
  float hue = u * 3.0 + 0.5;
  vec3 ridgeColor = mix(
    vec3(0.10, 0.55, 0.95),
    vec3(0.95, 0.40, 0.85),
    smoothstep(-0.2, 0.2, u)
  );
  col = mix(col, ridgeColor, smoothstep(0.05, 0.3, abs(u)));
  // Edge lines: bright where gradient magnitude is high.
  float edge = length(vec2(dx, dy));
  col += vec3(0.95, 0.85, 1.0) * smoothstep(0.01, 0.05, edge);
  // Audio modulation.
  float bassAvg = (uBass.x + uBass.y + uBass.z) / 3.0;
  float midAvg = (uMid.x + uMid.y + uMid.z) / 3.0;
  col *= 1.0 + bassAvg * 0.4;
  col += vec3(0.95, 0.65, 0.20) * midAvg * 0.2;
  outColor = vec4(col, 1.0);
}
`;

function createStateTexture(gl: WebGL2RenderingContext, size: number, seed: number): WebGLTexture {
  const tex = gl.createTexture();
  if (!tex) throw new Error("Failed to create texture");
  gl.bindTexture(gl.TEXTURE_2D, tex);
  const pixels = new Float32Array(size * size * 4);
  let s = seed | 0;
  function rand() {
    s = (s + 0x6D2B79F5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }
  for (let i = 0; i < size * size; i++) {
    const x = (i % size) / size * 2.0 - 1.0;
    const y = Math.floor(i / size) / size * 2.0 - 1.0;
    // Initial shape: a complex closed curve.
    // r(θ) = 0.5 + 0.3 * cos(5θ) + 0.2 * sin(7θ + π/4)
    const theta = Math.atan2(y, x);
    const r = 0.5 + 0.15 * Math.cos(5 * theta) + 0.1 * Math.sin(7 * theta + Math.PI / 4);
    const dist = Math.sqrt(x * x + y * y) - r;
    pixels[i * 4 + 0] = dist;
    pixels[i * 4 + 1] = 0;
    pixels[i * 4 + 2] = 0;
    pixels[i * 4 + 3] = 1;
  }
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, size, size, 0, gl.RGBA, gl.FLOAT, pixels);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return tex;
}

function attachFbo(gl: WebGL2RenderingContext, tex: WebGLTexture, size: number): WebGLFramebuffer {
  const fb = gl.createFramebuffer();
  if (!fb) throw new Error("Failed to create framebuffer");
  gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
  gl.viewport(0, 0, size, size);
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return fb;
}

export function createCurvatureFlowPipeline(gl: WebGL2RenderingContext, size = 96): CurvatureFlowPipeline | null {
  const updateProgram = linkProgram(gl, VERT_SRC, UPDATE_FRAG_SRC, POST_ATTRIBS);
  const renderProgram = linkProgram(gl, VERT_SRC, RENDER_FRAG_SRC, POST_ATTRIBS);
  if (!updateProgram || !renderProgram) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const tex0 = createStateTexture(gl, size, 42);
  const tex1 = createStateTexture(gl, size, 42);
  const fb0 = attachFbo(gl, tex0, size);
  const fb1 = attachFbo(gl, tex1, size);

  return {
    updateProgram, renderProgram, vao, vbo,
    stateTextures: [tex0, tex1],
    stateBuffers: [fb0, fb1],
    pingIdx: 0,
    bands: new Float32Array(9),
    gridSize: size,
  };
}

export function computeCurvatureFlowBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeCurvatureFlowRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function drawCurvatureFlow(
  gl: WebGL2RenderingContext,
  pipe: CurvatureFlowPipeline,
  width: number,
  height: number,
  t: number,
): void {
  if (!pipe.updateProgram || !pipe.renderProgram) return;
  const size = pipe.gridSize;
  for (let i = 0; i < 8; i++) {
    const srcIdx = pipe.pingIdx;
    const dstIdx = 1 - pipe.pingIdx;
    const srcTex = pipe.stateTextures[srcIdx];
    const dstFb = pipe.stateBuffers[dstIdx];
    if (!srcTex || !dstFb) continue;
    gl.bindFramebuffer(gl.FRAMEBUFFER, dstFb);
    gl.viewport(0, 0, size, size);
    gl.useProgram(pipe.updateProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, srcTex);
    const uPrev = gl.getUniformLocation(pipe.updateProgram, "uPrevState");
    if (uPrev) gl.uniform1i(uPrev, 0);
    const uTime = gl.getUniformLocation(pipe.updateProgram, "uTime");
    if (uTime) gl.uniform1f(uTime, t);
    gl.bindVertexArray(pipe.vao);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.bindVertexArray(null);
    pipe.pingIdx = dstIdx;
  }
  const finalTex = pipe.stateTextures[pipe.pingIdx];
  if (!finalTex) return;
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  gl.viewport(0, 0, width, height);
  gl.clearColor(0, 0, 0, 1);
  gl.clear(gl.COLOR_BUFFER_BIT);
  gl.useProgram(pipe.renderProgram);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, finalTex);
  const uState = gl.getUniformLocation(pipe.renderProgram, "uState");
  if (uState) gl.uniform1i(uState, 0);
  const uResolution = gl.getUniformLocation(pipe.renderProgram, "uResolution");
  if (uResolution) gl.uniform2f(uResolution, width, height);
  const uTime = gl.getUniformLocation(pipe.renderProgram, "uTime");
  if (uTime) gl.uniform1f(uTime, t);
  const uBass = gl.getUniformLocation(pipe.renderProgram, "uBass");
  if (uBass) gl.uniform3f(uBass, pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  const uMid = gl.getUniformLocation(pipe.renderProgram, "uMid");
  if (uMid) gl.uniform3f(uMid, pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  const uHigh = gl.getUniformLocation(pipe.renderProgram, "uHigh");
  if (uHigh) gl.uniform3f(uHigh, pipe.bands[6], pipe.bands[7], pipe.bands[8]);

    gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
